import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import { BudgetInput } from './Input';
import Histogram from '@spa/shared/components/Histogram';
import { Range } from '@spa/shared/components/Slider/range';
import { usePriceRange } from '@spa/shared/hooks/priceRange';
import { Container, ValuesContainer, ValuesSeparator } from './styles';
import { toMoneyCoreFormat, fromMoneyCoreFormat } from '@spa/shared/utils/money';

const getInitialRange = ([start, end], [min, max]) => [start || min, end || max];

const RangeFilter = ({
  start,
  end,
  startName,
  endName,
  min = 0,
  max = 100,
  onChange,
  histogram = {},
  currencyCode = 'EUR'
}) => {
  const initialRange = getInitialRange([start, end], [min, max]);

  const [histogramRange, setHistogramRange] = useState(initialRange);
  const [sliderRange, setSliderRange] = useState(initialRange.map(fromMoneyCoreFormat));
  const { t } = useTranslation('translations');

  const updateFilters = range => {
    const [start, end] = range.map(toMoneyCoreFormat);
    const cappedRangeWithCents = [Math.max(start, min), Math.min(end, max)];
    const cappedRangeWithoutCents = cappedRangeWithCents.map(fromMoneyCoreFormat);

    setHistogramRange(cappedRangeWithCents);
    setSliderRange(cappedRangeWithoutCents);

    onChange({ [startName]: cappedRangeWithCents[0], [endName]: cappedRangeWithCents[1] });
  };

  const { displayedRange, setDisplayedRange, onStartChange, onEndChange } = usePriceRange(
    initialRange,
    [min, max],
    updateFilters
  );

  // reset to initial state when filters are cleared
  useEffect(() => {
    const range = getInitialRange([start, end], [min, max]);
    const rangeWithoutCents = range.map(fromMoneyCoreFormat);

    setHistogramRange(range);
    setDisplayedRange(rangeWithoutCents);
    setSliderRange(rangeWithoutCents);
  }, [start, end, min, max]);

  const onSliderChange = (isDragging, newRange) => {
    setDisplayedRange(newRange);
    setHistogramRange(newRange.map(toMoneyCoreFormat));

    if (!isDragging) {
      updateFilters(newRange);
    }
  };

  const onPriceStartInputChange = e => {
    const {
      target: { value }
    } = e;

    onStartChange(value);
  };

  const onPriceEndInputChange = e => {
    const {
      target: { value }
    } = e;

    onEndChange(value);
  };

  const onPriceInputBlur = () => setDisplayedRange(histogramRange.map(fromMoneyCoreFormat));

  const onPriceInputStartFocus = () => setDisplayedRange(['', displayedRange[1]]);
  const onPriceInputEndFocus = () => setDisplayedRange([displayedRange[0], '']);

  return (
    <Container>
      <Histogram data={histogram} range={[...histogramRange]} />
      <Range
        min={fromMoneyCoreFormat(min)}
        max={fromMoneyCoreFormat(max)}
        valueMin={parseInt(sliderRange[0], 10)}
        valueMax={parseInt(sliderRange[1], 10)}
        onChange={onSliderChange}
        step={10}
      />
      <ValuesContainer>
        <BudgetInput
          name={startName}
          value={displayedRange[0]}
          label={t('filters.sections.budget.label.min_price')}
          currencyCode={currencyCode}
          onBlur={onPriceInputBlur}
          onFocus={onPriceInputStartFocus}
          onChange={onPriceStartInputChange}
        />
        <ValuesSeparator />
        <BudgetInput
          name={endName}
          value={displayedRange[1]}
          label={t('filters.sections.budget.label.max_price')}
          currencyCode={currencyCode}
          onBlur={onPriceInputBlur}
          onFocus={onPriceInputEndFocus}
          onChange={onPriceEndInputChange}
        />
      </ValuesContainer>
    </Container>
  );
};

RangeFilter.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  start: PropTypes.number,
  end: PropTypes.number,
  startName: PropTypes.string.isRequired,
  endName: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  scaleFactor: PropTypes.number,
  histogram: PropTypes.object,
  currencyCode: PropTypes.string
};

export default RangeFilter;
